import React from 'react';
import { Typography } from 'antd';
import { useLanguage } from '../store/LanguageContext';

const { Title, Paragraph } = Typography;

/* ── 章节标记映射 ── */
const SECTION_MARKERS = [
  { emoji: '⭐', labelKey: 'detail.sectionKeyFeatures' },
  { emoji: '💰', labelKey: 'detail.sectionPricing' },
  { emoji: '⚙️', labelKey: 'detail.sectionUseCases' },
  { emoji: '🙋‍♂️', labelKey: 'detail.sectionTargetUsers' },
  { emoji: '🙋', labelKey: 'detail.sectionTargetUsers' },
];

/* ── 项目符号前缀 ── */
const BULLET_PREFIXES = ['🧩', '🟢', '•', '✅', '🔹', '🔸', '📌', '💡', '🎯'];

interface Props {
  description: string;
  toolName?: string;
}

interface Section {
  heading: string;
  emoji: string;
  content: string[];
}

/**
 * 解析 topai.tools 抓取的结构化描述，渲染为美观的卡片式布局
 */
function parseDescription(text: string, t: (key: string) => string): { intro: string; sections: Section[] } {
  if (!text || text.length < 300) {
    return { intro: text || '', sections: [] };
  }

  // 用 emoji section marker 做分割
  const sectionRegex = /(⭐\s*Key features|💰\s*Pricing|⚙️\s*Use cases|🙋‍♂️\s*Who|🙋\s*Who)/gi;
  const parts = text.split(sectionRegex);

  // 第一段是介绍
  let intro = (parts[0] || '').trim();
  // 去掉 "What is X?" 开头换成更好的格式
  intro = intro.replace(/^What is [^?\n]+\?\s*/i, '').trim();

  const sections: Section[] = [];

  for (let i = 1; i < parts.length; i += 2) {
    const header = (parts[i] || '').trim();
    const body = (parts[i + 1] || '').trim();

    let emoji = '';
    let label = '';
    for (const m of SECTION_MARKERS) {
      if (header.includes(m.emoji)) {
        emoji = m.emoji;
        label = t(m.labelKey);
        break;
      }
    }

    // 将 body 按行或句子合理分割
    const lines = splitIntoLines(body);
    if (lines.length > 0) {
      sections.push({ heading: label || header, emoji, content: lines });
    }
  }

  return { intro, sections };
}

/** 智能拆分：按换行、项目符号、或句号+大写字母分割 */
function splitIntoLines(text: string): string[] {
  // 先按真实换行分
  let lines = text.split(/\n/).map(l => l.trim()).filter(Boolean);

  // 如果只有一行但很长，进一步按 bullet emoji 分割
  if (lines.length === 1 && lines[0].length > 200) {
    const bulletRe = new RegExp(`(${BULLET_PREFIXES.map(b => escapeRegex(b)).join('|')})`, 'g');
    const raw = lines[0];
    const splits = raw.split(bulletRe).filter(Boolean);

    if (splits.length > 1) {
      lines = [];
      let current = '';
      for (const s of splits) {
        if (BULLET_PREFIXES.includes(s.trim())) {
          if (current) lines.push(current.trim());
          current = s;
        } else {
          current += s;
        }
      }
      if (current) lines.push(current.trim());
    }
  }

  return lines;
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isBullet(line: string): boolean {
  return BULLET_PREFIXES.some(p => line.startsWith(p));
}

/** 清理 HTML 实体 */
function cleanText(text: string): string {
  return text
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const DescriptionRenderer: React.FC<Props> = ({ description, toolName }) => {
  const { t } = useLanguage();
  if (!description) return null;

  const isRich = description.length >= 300;

  if (!isRich) {
    return (
      <Paragraph style={{ fontSize: 15, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
        {description}
      </Paragraph>
    );
  }

  const { intro, sections } = parseDescription(description, t);

  return (
    <div className="desc-renderer">
      {/* 介绍段落 */}
      {intro && (
        <Paragraph style={{ fontSize: 15, lineHeight: 1.9, marginBottom: 20, color: '#333' }}>
          {cleanText(intro)}
        </Paragraph>
      )}

      {/* 各功能区块 */}
      {sections.map((sec, idx) => (
        <div
          key={idx}
          style={{
            marginBottom: 20,
            padding: '16px 20px',
            background: idx % 2 === 0 ? '#f8fafc' : '#fefce8',
            borderRadius: 10,
            borderLeft: `4px solid ${
              sec.emoji === '⭐' ? '#faad14' :
              sec.emoji === '💰' ? '#52c41a' :
              sec.emoji === '⚙️' ? '#1890ff' :
              '#722ed1'
            }`,
          }}
        >
          <Title
            level={5}
            style={{
              marginBottom: 12,
              fontSize: 16,
              color: '#1a1a1a',
            }}
          >
            {sec.emoji} {sec.heading}
          </Title>

          {sec.content.map((line, li) => {
            const cleaned = cleanText(line);
            if (!cleaned) return null;

            if (isBullet(cleaned)) {
              return (
                <div
                  key={li}
                  style={{
                    padding: '6px 0 6px 8px',
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: '#444',
                  }}
                >
                  {cleaned}
                </div>
              );
            }

            return (
              <Paragraph
                key={li}
                style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 8, color: '#444' }}
              >
                {cleaned}
              </Paragraph>
            );
          })}
        </div>
      ))}

      <style>{`
        .desc-renderer .ant-typography { margin-bottom: 8px; }
      `}</style>
    </div>
  );
};

export default DescriptionRenderer;
